import type { ReactNode } from 'react';
import { useAuth } from '../context/AuthContext';

type Role = 'reader' | 'author' | 'moderator' | 'admin';

const HIERARCHY: Record<Role, number> = {
  reader: 0,
  author: 1,
  moderator: 2,
  admin: 3,
};

interface RoleGuardProps {
  minRole: Exclude<Role, 'reader'>;
  fallback: ReactNode;
  loadingFallback?: ReactNode;
  children: ReactNode;
}

export default function RoleGuard({
  minRole,
  fallback,
  loadingFallback = null,
  children,
}: RoleGuardProps) {
  const { user, isLoading } = useAuth();
  // Don't evaluate role until auth has rehydrated — otherwise a valid moderator/admin
  // sees the fallback on page refresh before their token/user has been restored.
  if (isLoading) {
    return <>{loadingFallback}</>;
  }
  // `user` union includes a pre-hydration partial that omits `role` from its type —
  // read through a narrow lens rather than narrowing the whole union here.
  const userRole =
    ((user as { role?: Role } | null)?.role as Role | undefined) ?? 'reader';
  const userLevel = HIERARCHY[userRole] ?? 0;
  const required = HIERARCHY[minRole];
  if (userLevel >= required) {
    return <>{children}</>;
  }
  return <>{fallback}</>;
}
